import React, { SetStateAction, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';
import Lottie from 'lottie-react';
import Resizer from 'react-image-file-resizer';

import Column from 'components/common/Column';
import Text from 'components/common/Text';
import { ICON_CLOSE_OUTLINE, ICON_PRODUCT_CAMERA } from 'assets/icons';
import Row from 'components/common/Row';
import { ProductParamInput } from 'containers/ProductRegist/hooks/types';
import Textarea from '../common/Textarea';
import {
  callWebviewHandler,
  isWebview,
  WebviewOperatorType,
} from 'utils/webview';
import HackleManager from 'utils/hackle';
import lottie from 'assets/lotties/image_upload_lottie.json';
import TitleContainer from '../TitleContainer';
import { ICON_PLUS_MONO } from 'assets/icons';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 40px;
  width: 100%;
`;

const ProductNameForm = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 12px 0 0;
  width: 100%;

  border-radius: 8px;
  border: 1px solid #dbdcdf;
  background: #fff;
`;

const ImageUploadForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  border-radius: 16px;
  border: 1px solid #f4f4f5;
  background: #f7f7f8;

  color: var(--gray-gray-06, #989ba2);

  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: -0.6px;
  cursor: pointer;

  input {
    position: absolute;
    display: none;
    border: 1px solid red;
    width: 100%;
    height: 100%;
  }
`;

const ImageContainer = styled.div<{ $width: number; $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  width: ${props =>
    props.$isMobile ? (props.$width - 12) / 2 : (props.$width - 24) / 3}px;
  height: ${props =>
    props.$isMobile ? (props.$width - 12) / 2 : (props.$width - 24) / 3}px;

  border-radius: 16px;
  background-color: #f4f4f5;
`;

const DeleteContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  padding: 6px;
  border-radius: 50%;
  background: rgba(46, 47, 51, 0.88);
`;

const RepresentativeBadge = styled.div`
  position: absolute;
  display: flex;
  left: 8px;
  top: 8px;
  padding: 6px 8px;
  align-items: center;
  gap: 8px;
  border-radius: 99px;
  border: 1px solid rgba(55, 56, 60, 0.16);
  background: #fff;
  color: #1b1c1e;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: -0.2px;
`;

const ImageNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  background: #feeee5;
  color: #f55a00;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.6px;

  ul {
    margin: 0;
    padding-left: 21px;
    list-style: disc;
  }

  li {
    margin: 0;
  }
`;

interface ChapterTwoIProps {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  imageThumbnail: string[];
  setImageThumbnail: React.Dispatch<SetStateAction<string[]>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<SetStateAction<boolean>>;
  handleGenerateContentUpload: (uploadInfo: {
    file: File;
    fileName: string;
  }) => Promise<void>;
  handleDeleteImage: (idx: number) => void;
}

const ChapterTwo: React.FC<ChapterTwoIProps> = props => {
  const {
    isMobile,
    productParamInput,
    imageThumbnail,
    setImageThumbnail,
    handleOnChangeProductParamInput,
    isLoading,
    setIsLoading,
    handleGenerateContentUpload,
    handleDeleteImage,
  } = props;

  const params = useParams<{ id: string }>();

  const { width, ref } = useResizeDetector();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_FILES = 5; // 파일 최대 개수

  const handleFileChange = async (event: any) => {
    await Resizer.imageFileResizer(
      event.target.files[0],
      375 * 3,
      375 * 3,
      'png',
      80,
      0,
      (file: File) => {
        setIsLoading(true);
        handleGenerateContentUpload({
          file,
          fileName: file.name,
        });
      },
      'file',
    );
  };

  const handleImageUploadClick = () => {
    if (imageThumbnail.length >= MAX_FILES) {
      (window as any).showToast('최대 5개만 업로드 가능합니다', 3000);

      return;
    }

    if (fileInputRef.current) {
      if (isWebview()) {
        callWebviewHandler(
          WebviewOperatorType.UpdateMessageImageFileHandler,
          undefined,
          'handleImageUpload',
        );
      } else {
        fileInputRef.current.click();
      }
    }
  };

  useEffect(() => {
    HackleManager.logCreateStepThreeProductNameImageView(
      params.id !== 'draft' ? Number(params.id) : undefined,
    );
  }, []);

  const renderContents = (): JSX.Element => {
    return (
      <Container ref={ref}>
        <React.Fragment>
          <Column gap="8px" align="flex-start">
            <Row gap="6px" align="center">
              <Text color="#1b1c1e" size="17px" weight={500}>
                상품명
              </Text>
              <Text color="#FF4242" size="17px" weight={500}>
                *
              </Text>
            </Row>
            <ProductNameForm>
              <Textarea
                placeholder="프립명을 입력해 주세요"
                target="title"
                value={productParamInput.title}
                maxLength={40}
                hideCounter
                onChange={handleOnChangeProductParamInput}
                style={{
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                }}
              />
              <Text color="#989ba2" size="14px" weight={400}>
                {productParamInput.title.length}/40
              </Text>
            </ProductNameForm>
          </Column>
        </React.Fragment>
        <Column gap="16px" align="flex-start">
          <Row gap="6px" align="center">
            <Text color="#1b1c1e" size="17px" weight={500}>
              상품이미지
            </Text>
            <Text color="#FF4242" size="17px" weight={500}>
              *
            </Text>
          </Row>
          <ImageNotice data-node-id="67:20409">
            <ul>
              <li>
                첫번째 이미지는 대표 이미지로 사용되며, 텍스트, 로고를 표시할 수
                없어요.
              </li>
              <li>용량은 10MB 이하 확장자는 JPG, PNG만 가능해요.</li>
              <li>
                활동이 잘 드러나는 사진을 최소 1장 ~ 최대 5장까지 등록해주세요.
              </li>
            </ul>
          </ImageNotice>
          <Row
            gap="12px"
            justifyContent={isMobile ? 'space-between' : 'flex-start'}
            style={{
              display: 'grid',
              // flexWrap: 'wrap',
              gridTemplateColumns: `repeat(auto-fill, minmax(${
                isMobile ? (width - 12) / 2 : (width - 24) / 3
              }px, auto))`,
              width: '100%',
            }}
          >
            <ImageUploadForm
              onClick={() => {
                handleImageUploadClick();
              }}
              style={{
                width: `${isMobile ? (width - 12) / 2 : (width - 24) / 3}px`,
                height: `${isMobile ? (width - 12) / 2 : (width - 24) / 3}px`,
              }}
            >
              <img src={ICON_PLUS_MONO} alt="image upload icon" />
              {imageThumbnail.length}/5
              <input
                ref={fileInputRef}
                type="file"
                multiple={false}
                onChange={handleFileChange}
                accept=".jpg, .png, .jpeg"
              />
            </ImageUploadForm>
            {imageThumbnail.map((item, idx) => {
              return (
                <ImageContainer $width={width} $isMobile={isMobile} key={idx}>
                  {idx === 0 && (
                    <RepresentativeBadge>대표사진</RepresentativeBadge>
                  )}
                  <img
                    src={item}
                    alt="image thumbnail"
                    key={idx}
                    style={{
                      width: `${
                        isMobile ? (width - 12) / 2 : (width - 12) / 3
                      }px`,
                      height: `${
                        isMobile ? (width - 12) / 2 : (width - 12) / 3
                      }px`,
                      borderRadius: '16px',
                      border: '1px solid rgba(112, 115, 124, 0.05)',
                      objectFit: 'cover',
                    }}
                  />
                  <DeleteContainer
                    onClick={() => {
                      handleDeleteImage(idx);
                    }}
                  >
                    <img src={ICON_CLOSE_OUTLINE} width={16} height={16} />
                  </DeleteContainer>
                </ImageContainer>
              );
            })}
            {isLoading && (
              <ImageContainer $width={width} $isMobile={isMobile}>
                <Lottie animationData={lottie} style={{ width: '50px' }} />
              </ImageContainer>
            )}
          </Row>
        </Column>
      </Container>
    );
  };

  return (
    <Column>
      <TitleContainer
        title="상품명과 사진을 추가해 주세요"
        isMobile={isMobile}
      />
      {renderContents()}
    </Column>
  );
};

export default ChapterTwo;
